import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountApi } from '../../api';
import { copyToClipboard } from '../../utils/clipboard';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, Table, Pagination, Modal, FormItem, message, formatDate, Tooltip } from '../common';
import * as XLSX from 'xlsx';

const AccountManager = ({ mode = 'admin' }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = mode === 'admin';
    const [accounts, setAccounts] = useState([]);
    const [owners, setOwners] = useState([]);
    const [salesPersons, setSalesPersons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0 });

    // Developer should only see their own accounts
    // 支持多选筛选
    const [filters, setFilters] = useState({
        status: [],  // 改为数组支持多选
        keyword: '',
        developer_id: isAdmin ? [] : [user.id],  // 改为数组支持多选
        sales_id: []  // 新增：销售人筛选
    });

    const [sort, setSort] = useState({ field: 'updated_at', order: 'desc' });
    const [modalVisible, setModalVisible] = useState(false);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [importErrors, setImportErrors] = useState([]);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [detailAccount, setDetailAccount] = useState(null);
    const [formData, setFormData] = useState({
        email: '', password: '', two_factor_code: '', recovery_email: '', recovery_phone: '', developer_id: user.id, status: 'available', remark: ''
    });
    const [importData, setImportData] = useState({ text: '', developer_id: user.id });
    const [tokenLoading, setTokenLoading] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [batchAction, setBatchAction] = useState('status'); // 'status' | 'owner'
    const [batchStatus, setBatchStatus] = useState('');
    const [batchOwner, setBatchOwner] = useState('');
    const [exportFormat, setExportFormat] = useState('format1'); // 'format1' | 'format2' | 'custom'
    const [customFormatTemplate, setCustomFormatTemplate] = useState('{email} ---- {password} ---- {two_factor_code}');

    // 快捷销售相关状态
    const [quickSaleModalVisible, setQuickSaleModalVisible] = useState(false);
    const [quickSaleAccount, setQuickSaleAccount] = useState(null);
    const [quickSaleData, setQuickSaleData] = useState({
        customer_name: '',
        customer_email: '',
        amount: '',
        sales_id: '',
        remark: ''
    });

    // 随机排序相关状态
    const [isRandomMode, setIsRandomMode] = useState(false);

    // 下拉菜单显示状态
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
    const [salesDropdownOpen, setSalesDropdownOpen] = useState(false);

    const fetchAccounts = async (useRandom = false, customFilters = null) => {
        setLoading(true);
        try {
            // 使用传入的自定义筛选条件，或使用当前状态的 filters
            const activeFilters = customFilters || filters;

            const params = {
                page: pagination.page,
                per_page: pagination.per_page,
                keyword: activeFilters.keyword,
                sort_by: sort.field,
                order: sort.order
            };

            // 多选筛选：转为逗号分隔的字符串
            if (activeFilters.status.length > 0) {
                params.status = activeFilters.status.join(',');
            }
            if (isAdmin && activeFilters.developer_id.length > 0) {
                params.developer_id = activeFilters.developer_id.join(',');
            }
            if (activeFilters.sales_id.length > 0) {
                params.sales_id = activeFilters.sales_id.join(',');
            }

            // Safety: ensure developer_id is locked if not admin
            if (!isAdmin) {
                params.developer_id = user.id;
            }

            Object.keys(params).forEach(k => !params[k] && delete params[k]);

            let res;
            if (useRandom) {
                res = await accountApi.getRandom(params);
                setIsRandomMode(true);
            } else {
                res = await accountApi.getList(params);
                setIsRandomMode(false);
            }
            setAccounts(res.data.items);
            setPagination(prev => ({ ...prev, total: res.data.total }));
        } catch (err) {
            message.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchOwners = async () => {
        if (!isAdmin && !user.has_role('developer')) return;
        try {
            const res = await accountApi.getOwners();
            setOwners(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchSalesPersons = async () => {
        if (!isAdmin && !user.has_role('developer')) return;
        try {
            const res = await accountApi.getSalesPersons();
            setSalesPersons(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchOwners();
        fetchSalesPersons();
    }, [isAdmin]);
    useEffect(() => { fetchAccounts(); }, [pagination.page, sort]);

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchAccounts();
    };

    const handleRandomSort = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchAccounts(true);
    };

    const handleSort = (field, order) => {
        setSort({ field, order });
        setIsRandomMode(false);
    };

    const handleCreate = () => {
        setEditingAccount(null);
        setFormData({
            email: '', password: '', two_factor_code: '', recovery_email: '', recovery_phone: '',
            developer_id: user.id,
            status: 'pending_upgrade', remark: ''
        });
        setModalVisible(true);
    };

    const handleEdit = (account) => {
        setEditingAccount(account);
        setFormData({ ...account });
        setModalVisible(true);
    };

    const handleDetail = (account) => {
        setDetailAccount(account);
        setDetailModalVisible(true);
    };

    const handleDelete = async (account) => {
        if (!window.confirm(`确定删除账号 ${account.email} 吗？`)) return;
        try {
            await accountApi.delete(account.id);
            message.success('删除成功');
            fetchAccounts();
        } catch (err) {
            message.error(err.message);
        }
    };

    const handleSubmit = async () => {
        try {
            const data = { ...formData };
            if (!isAdmin) {
                data.developer_id = user.id; // Force self
            }

            if (editingAccount) {
                await accountApi.update(editingAccount.id, data);
                message.success('更新成功');
            } else {
                await accountApi.create(data);
                message.success('创建成功');
            }
            setModalVisible(false);
            fetchAccounts();
        } catch (err) {
            message.error(err.message);
        }
    };

    const handleGet2faToken = async (account) => {
        if (!account.two_factor_code || account.two_factor_code === '******') {
            message.error('该账号未设置2FA通行码');
            return;
        }
        setTokenLoading(prev => ({ ...prev, [account.id]: true }));
        try {
            const res = await accountApi.get2faToken(account.id);
            const token = res.data.token;
            const success = await copyToClipboard(token);
            if (success) {
                message.success(`验证码: ${token} (已复制到剪贴板)`);
            } else {
                message.success(`验证码: ${token} (复制失败，请手动复制)`);
            }
        } catch (err) {
            message.error(err.message);
        } finally {
            setTokenLoading(prev => ({ ...prev, [account.id]: false }));
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await accountApi.update(id, { status: newStatus });
            message.success('状态更新成功');
            setAccounts(prev => prev.map(acc => acc.id === id ? { ...acc, status: newStatus } : acc));
        } catch (err) {
            message.error(err.message || '更新状态失败');
        }
    };

    const handleQuickCopy = async (text, label) => {
        if (!text) return;
        const success = await copyToClipboard(text);
        if (success) {
            message.success(`${label}已复制`);
        } else {
            message.error('复制失败');
        }
    };

    const handleCopyDeliveryInfo = async (account) => {
        const text = `谷歌邮箱： ${account.email}
密码： ${account.password}
2FA通行码： ${account.two_factor_code || '-'}
验证邮箱： ${account.recovery_email || '-'}`;

        const success = await copyToClipboard(text);
        if (success) {
            message.success('发货信息已复制到剪贴板:\n' + text);
        } else {
            message.error('复制失败，请重试');
        }
    };

    const handleOpenImport = () => {
        setImportData({ text: '', developer_id: user.id });
        setImportModalVisible(true);
    };

    const handleImport = async () => {
        if (!importData.text.trim()) {
            message.error('请输入导入内容');
            return;
        }
        try {
            const data = { ...importData };
            if (!isAdmin) {
                data.developer_id = user.id;
            }
            const res = await accountApi.batchImport(data);
            setImportModalVisible(false);

            if (res.data.errors && res.data.errors.length > 0) {
                setImportErrors(res.data.errors);
                setErrorModalVisible(true);
            } else {
                message.success(res.message);
            }

            fetchAccounts();
            setImportData({ text: '', developer_id: user.id });
        } catch (err) {
            message.error(err.message);
        }
    };

    const handleBatchUpdate = async () => {
        if (selectedRowKeys.length === 0) {
            message.error('请先选择账号');
            return;
        }

        const data = { account_ids: selectedRowKeys };
        let confirmMsg = '';

        if (batchAction === 'status') {
            if (!batchStatus) {
                message.error('请选择目标状态');
                return;
            }
            data.status = batchStatus;
            confirmMsg = `确定将选中的 ${selectedRowKeys.length} 个账号状态修改为 ${statusMap[batchStatus]?.text || batchStatus} 吗？`;
        } else if (batchAction === 'owner') {
            if (!batchOwner) {
                message.error('请选择新所属者');
                return;
            }
            data.developer_id = parseInt(batchOwner);
            const ownerName = owners.find(o => o.id === data.developer_id)?.real_name || batchOwner;
            confirmMsg = `确定将选中的 ${selectedRowKeys.length} 个账号的所属者修改为 ${ownerName} 吗？`;
        }

        if (!window.confirm(confirmMsg)) return;

        try {
            const res = await accountApi.batchUpdate(data);
            message.success(res.message);
            setSelectedRowKeys([]);
            setBatchStatus('');
            setBatchOwner('');
            fetchAccounts();
        } catch (err) {
            message.error(err.message);
        }
    };

    // 快捷销售相关函数
    const handleOpenQuickSale = (account) => {
        setQuickSaleAccount(account);
        setQuickSaleData({
            customer_name: user.real_name || user.username,
            customer_email: user.email || '',
            amount: '',
            sales_id: user.id,
            remark: ''
        });
        setQuickSaleModalVisible(true);
    };

    const handleQuickSale = async () => {
        if (!quickSaleData.customer_email) {
            message.error('请输入客户邮箱');
            return;
        }
        if (!quickSaleData.amount) {
            message.error('请输入销售金额');
            return;
        }

        try {
            const res = await accountApi.quickSale(quickSaleAccount.id, {
                ...quickSaleData,
                amount: parseFloat(quickSaleData.amount)
            });

            // 复制发货信息到剪贴板
            const account = quickSaleAccount;
            const deliveryText = `谷歌邮箱： ${account.email}
密码： ${account.password}
2FA通行码： ${account.two_factor_code || '-'}
验证邮箱： ${account.recovery_email || '-'}`;

            await copyToClipboard(deliveryText);
            message.success('销售成功！发货信息已复制到剪贴板');

            setQuickSaleModalVisible(false);

            // 获取销售记录ID并跳转到销售页面
            const salesRecordId = res.data?.sales_record_id;
            const salesPath = isAdmin ? '/admin/sales' : '/developer/sales';

            // 跳转到销售页面，带上查询参数
            if (salesRecordId) {
                navigate(`${salesPath}?keyword=${encodeURIComponent(account.email)}`);
            } else {
                navigate(`${salesPath}?keyword=${encodeURIComponent(account.email)}`);
            }
        } catch (err) {
            message.error(err.message);
        }
    };

    // 多选筛选处理函数
    const handleMultiSelectChange = (filterKey, value) => {
        setFilters(prev => {
            const currentValues = prev[filterKey];
            if (currentValues.includes(value)) {
                return { ...prev, [filterKey]: currentValues.filter(v => v !== value) };
            } else {
                return { ...prev, [filterKey]: [...currentValues, value] };
            }
        });
    };

    const handleClearFilter = (filterKey) => {
        setFilters(prev => ({ ...prev, [filterKey]: [] }));
    };

    // 快捷筛选：我要卖号
    const handleQuickSell = () => {
        const newFilters = {
            ...filters,
            status: ['pending_sale'],
            developer_id: [user.id]
        };
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
        // 直接传入新的筛选条件，避免状态异步问题
        fetchAccounts(false, newFilters);
    };

    // 快捷筛选：我要造号
    const handleQuickCreate = () => {
        const newFilters = {
            ...filters,
            status: ['pending_upgrade'],
            developer_id: [user.id]
        };
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
        // 直接传入新的筛选条件，避免状态异步问题
        fetchAccounts(false, newFilters);
    };

    // 关闭所有下拉菜单
    const closeAllDropdowns = () => {
        setStatusDropdownOpen(false);
        setOwnerDropdownOpen(false);
        setSalesDropdownOpen(false);
    };

    // 一键导出选中账号
    const handleExport = async () => {
        if (selectedRowKeys.length === 0) {
            message.error('请先选择要导出的账号');
            return;
        }

        try {
            // 调用后端 API 获取选中账号的完整信息（包括敏感信息）
            const res = await accountApi.batchGetDetails(selectedRowKeys);
            const accountsData = res.data;

            if (!accountsData || accountsData.length === 0) {
                message.error('未获取到账号数据');
                return;
            }

            // 格式化数据用于复制到剪贴板（根据选择的格式）
            let clipboardText;
            if (exportFormat === 'format1') {
                // 格式1: 邮箱：xx 密码：xx 2FA：xxx
                clipboardText = accountsData.map(acc =>
                    `邮箱：${acc.email} 密码：${acc.password} 2FA：${acc.two_factor_code || '-'}`
                ).join('\n');
            } else if (exportFormat === 'format2') {
                // 格式2: email ---- pwd ---- email2 ---- 2fa
                clipboardText = accountsData.map(acc =>
                    `${acc.email} ---- ${acc.password} ---- ${acc.recovery_email || '-'} ---- ${acc.two_factor_code || '-'}`
                ).join('\n');
            } else {
                // 自定义格式: 使用模板替换变量
                clipboardText = accountsData.map(acc => {
                    let line = customFormatTemplate;
                    // 替换所有可用的变量
                    line = line.replace(/{email}/g, acc.email || '-');
                    line = line.replace(/{password}/g, acc.password || '-');
                    line = line.replace(/{two_factor_code}/g, acc.two_factor_code || '-');
                    line = line.replace(/{2fa}/g, acc.two_factor_code || '-');
                    line = line.replace(/{recovery_email}/g, acc.recovery_email || '-');
                    line = line.replace(/{recovery_phone}/g, acc.recovery_phone || '-');
                    line = line.replace(/{status}/g, statusMap[acc.status]?.text || acc.status);
                    line = line.replace(/{developer_name}/g, acc.developer_name || '-');
                    line = line.replace(/{remark}/g, acc.remark || '-');
                    line = line.replace(/{id}/g, acc.id || '-');
                    return line;
                }).join('\n');
            }

            // 复制到剪贴板
            const copySuccess = await copyToClipboard(clipboardText);

            // 生成 Excel 文件
            const excelData = accountsData.map(acc => ({
                '谷歌邮箱': acc.email,
                '密码': acc.password,
                '2FA通行码': acc.two_factor_code || '',
                '验证邮箱': acc.recovery_email || '',
                '验证手机': acc.recovery_phone || '',
                '状态': statusMap[acc.status]?.text || acc.status,
                '所属者': acc.developer_name || '',
                '备注': acc.remark || ''
            }));

            const ws = XLSX.utils.json_to_sheet(excelData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, '账号列表');

            // 设置列宽
            ws['!cols'] = [
                { wch: 30 }, // 邮箱
                { wch: 20 }, // 密码
                { wch: 20 }, // 2FA
                { wch: 30 }, // 验证邮箱
                { wch: 15 }, // 验证手机
                { wch: 10 }, // 状态
                { wch: 15 }, // 所属者
                { wch: 30 }  // 备注
            ];

            // 生成文件名（使用当前时间）
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filename = `谷歌账号导出_${timestamp}.xlsx`;

            // 下载文件
            XLSX.writeFile(wb, filename);

            if (copySuccess) {
                message.success(`成功导出 ${accountsData.length} 个账号，数据已复制到剪贴板并下载为 Excel 文件`);
            } else {
                message.success(`成功导出 ${accountsData.length} 个账号到 Excel 文件`);
            }
        } catch (err) {
            message.error(err.message || '导出失败');
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys
    };

    const statusMap = {
        pending_upgrade: { text: '待升级', class: 'tag-warning' },
        pending_sale: { text: '待销售', class: 'tag-info' },
        pending_settle: { text: '待结算', class: 'tag-warning' },
        settled: { text: '已结算', class: 'tag-success' },
        self_use: { text: '自用', class: 'tag-default' },
        disabled: { text: '不可用', class: 'tag-error' }
    };
    const roleMap = { admin: '管理员', developer: '开发者' };

    const columns = [
        { key: 'id', title: 'ID', width: '60px', sorter: true },
        {
            key: 'status',
            title: '状态',
            render: (v, row) => (
                <select
                    value={v}
                    onClick={e => e.stopPropagation()}
                    onChange={e => handleStatusChange(row.id, e.target.value)}
                    className={`tag-select ${statusMap[v]?.class || 'tag-default'}`}
                    style={{
                        border: 'none',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        textAlign: 'center',
                        fontWeight: '500'
                    }}
                >
                    {Object.entries(statusMap).map(([key, val]) => (
                        <option key={key} value={key} style={{ color: '#333', background: 'white' }}>
                            {val.text}
                        </option>
                    ))}
                </select>
            ),
            sorter: true
        },
        {
            key: 'email',
            title: '谷歌邮箱',
            sorter: true,
            render: (v) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                    <span>{v}</span>
                    <Tooltip text="复制邮箱">
                        <span
                            onClick={(e) => { e.stopPropagation(); handleQuickCopy(v, '邮箱'); }}
                            style={{ cursor: 'pointer', color: '#1890ff', fontSize: '14px' }}
                        >
                            📋
                        </span>
                    </Tooltip>
                </div>
            )
        },
        {
            key: 'password',
            title: '密码',
            render: (v) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{v}</span>
                    <Tooltip text="复制密码">
                        <span
                            onClick={(e) => { e.stopPropagation(); handleQuickCopy(v, '密码'); }}
                            style={{ cursor: 'pointer', color: '#1890ff', fontSize: '14px' }}
                        >
                            📋
                        </span>
                    </Tooltip>
                </div>
            )
        },
        isAdmin && { key: 'developer_name', title: '所属者' },
        { key: 'created_at', title: '创建时间', render: (v) => formatDate(v), sorter: true },
        { key: 'updated_at', title: '更新时间', render: (v) => formatDate(v), sorter: true },
        {
            key: 'actions', title: '操作', render: (_, row) => (
                <div className="action-buttons">
                    {/* 待销售状态显示销售按钮 */}
                    {row.status === 'pending_sale' && (
                        <button
                            className="btn btn-small btn-primary"
                            onClick={() => handleOpenQuickSale(row)}
                        >
                            销售
                        </button>
                    )}
                    {row.two_factor_code && row.two_factor_code !== '******' && (
                        <button
                            className="btn btn-small btn-success"
                            onClick={() => handleGet2faToken(row)}
                            disabled={tokenLoading[row.id]}
                        >
                            {tokenLoading[row.id] ? '...' : '验证码'}
                        </button>
                    )}
                    <button
                        className="btn btn-small btn-default"
                        onClick={() => handleCopyDeliveryInfo(row)}
                    >
                        发货信息
                    </button>
                    <button className="btn btn-small btn-default" onClick={() => handleDetail(row)}>详情</button>
                    <button className="btn btn-small btn-default" onClick={() => handleEdit(row)}>编辑</button>
                    <button className="btn btn-small btn-danger" onClick={() => handleDelete(row)}>删除</button>
                </div>
            )
        }
    ].filter(Boolean); // Clear out false values (like hidden Owner column)

    // 多选下拉组件
    const MultiSelect = ({ label, options, selectedValues, onChange, onClear, valueKey = 'id', labelKey = 'real_name' }) => (
        <div className="form-item" style={{ position: 'relative' }}>
            <div style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '6px 10px',
                minWidth: '150px',
                background: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px'
            }}>
                <span style={{ color: selectedValues.length > 0 ? '#333' : '#999' }}>
                    {selectedValues.length > 0 ? `${label} (${selectedValues.length})` : `全部${label}`}
                </span>
                {selectedValues.length > 0 && (
                    <span
                        onClick={(e) => { e.stopPropagation(); onClear(); }}
                        style={{ color: '#999', cursor: 'pointer' }}
                    >
                        ✕
                    </span>
                )}
            </div>
            <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: 1000,
                maxHeight: '200px',
                overflow: 'auto',
                display: 'none'
            }}
                className="multi-select-dropdown"
            >
                {options.map(opt => (
                    <label
                        key={opt[valueKey]}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0'
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={selectedValues.includes(opt[valueKey])}
                            onChange={() => onChange(opt[valueKey])}
                            style={{ marginRight: '8px' }}
                        />
                        {opt[labelKey] || opt.username}
                    </label>
                ))}
            </div>
            <style>{`
                .form-item:hover .multi-select-dropdown {
                    display: block !important;
                }
            `}</style>
        </div>
    );

    return (
        <div>
            <PageHeader title={isAdmin ? "账号管理" : "我的账号"} extra={
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn btn-default" onClick={handleOpenImport}>批量导入</button>
                    <button className="btn btn-primary" onClick={handleCreate}>新建账号</button>
                </div>
            } />

            <div className="search-bar" style={{ flexWrap: 'wrap', gap: '10px' }}>
                {/* 快捷操作按钮 */}
                <div style={{ display: 'flex', gap: '8px', marginRight: '10px' }}>
                    <button
                        className="btn btn-warning"
                        onClick={handleQuickSell}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        我要卖号
                    </button>
                    <button
                        className="btn btn-info"
                        onClick={handleQuickCreate}
                        style={{ whiteSpace: 'nowrap', background: '#17a2b8', borderColor: '#17a2b8', color: 'white' }}
                    >
                        我要造号
                    </button>
                </div>

                <div className="form-item">
                    <input placeholder="搜索邮箱" value={filters.keyword} onChange={e => setFilters({ ...filters, keyword: e.target.value })} />
                </div>

                {/* 状态多选 - 点击式 */}
                <div className="form-item" style={{ position: 'relative' }}>
                    <div
                        style={{
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '6px 10px',
                            minWidth: '100px',
                            background: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setStatusDropdownOpen(!statusDropdownOpen);
                            setOwnerDropdownOpen(false);
                            setSalesDropdownOpen(false);
                        }}
                    >
                        <span style={{ color: filters.status.length > 0 ? '#333' : '#999', fontSize: '13px' }}>
                            {filters.status.length > 0 ? `状态 (${filters.status.length})` : '全部状态'}
                        </span>
                        <span style={{ color: '#999', fontSize: '10px' }}>▼</span>
                    </div>
                    {statusDropdownOpen && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                marginTop: '4px',
                                background: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                zIndex: 1000,
                                padding: '8px',
                                minWidth: '200px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {Object.entries(statusMap).map(([key, val]) => (
                                    <span
                                        key={key}
                                        onClick={() => handleMultiSelectChange('status', key)}
                                        className={`tag ${val.class}`}
                                        style={{
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            border: filters.status.includes(key) ? '2px solid #1890ff' : '2px solid transparent',
                                            opacity: filters.status.includes(key) ? 1 : 0.7,
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {filters.status.includes(key) && '✓ '}{val.text}
                                    </span>
                                ))}
                            </div>
                            {filters.status.length > 0 && (
                                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eee', textAlign: 'right' }}>
                                    <span onClick={() => handleClearFilter('status')} style={{ color: '#1890ff', fontSize: '12px', cursor: 'pointer' }}>清除选择</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* 所属者多选 - 点击式 */}
                {isAdmin && (
                    <div className="form-item" style={{ position: 'relative' }}>
                        <div
                            style={{
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '6px 10px',
                                minWidth: '100px',
                                background: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '6px'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setOwnerDropdownOpen(!ownerDropdownOpen);
                                setStatusDropdownOpen(false);
                                setSalesDropdownOpen(false);
                            }}
                        >
                            <span style={{ color: filters.developer_id.length > 0 ? '#333' : '#999', fontSize: '13px' }}>
                                {filters.developer_id.length > 0 ? `所属者 (${filters.developer_id.length})` : '全部所属者'}
                            </span>
                            <span style={{ color: '#999', fontSize: '10px' }}>▼</span>
                        </div>
                        {ownerDropdownOpen && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    marginTop: '4px',
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    zIndex: 1000,
                                    padding: '8px',
                                    minWidth: '220px',
                                    maxHeight: '250px',
                                    overflow: 'auto'
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {owners.map(o => (
                                        <span
                                            key={o.id}
                                            onClick={() => handleMultiSelectChange('developer_id', o.id)}
                                            style={{
                                                padding: '4px 10px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                background: filters.developer_id.includes(o.id) ? '#e6f7ff' : '#f5f5f5',
                                                border: filters.developer_id.includes(o.id) ? '1px solid #1890ff' : '1px solid #d9d9d9',
                                                color: filters.developer_id.includes(o.id) ? '#1890ff' : '#666',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {filters.developer_id.includes(o.id) && '✓ '}{o.real_name || o.username}
                                        </span>
                                    ))}
                                </div>
                                {filters.developer_id.length > 0 && (
                                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eee', textAlign: 'right' }}>
                                        <span onClick={() => handleClearFilter('developer_id')} style={{ color: '#1890ff', fontSize: '12px', cursor: 'pointer' }}>清除选择</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* 销售人多选 - 点击式 */}
                {isAdmin && (
                    <div className="form-item" style={{ position: 'relative' }}>
                        <div
                            style={{
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '6px 10px',
                                minWidth: '100px',
                                background: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '6px'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSalesDropdownOpen(!salesDropdownOpen);
                                setStatusDropdownOpen(false);
                                setOwnerDropdownOpen(false);
                            }}
                        >
                            <span style={{ color: filters.sales_id.length > 0 ? '#333' : '#999', fontSize: '13px' }}>
                                {filters.sales_id.length > 0 ? `销售人 (${filters.sales_id.length})` : '全部销售人'}
                            </span>
                            <span style={{ color: '#999', fontSize: '10px' }}>▼</span>
                        </div>
                        {salesDropdownOpen && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    marginTop: '4px',
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    zIndex: 1000,
                                    padding: '8px',
                                    minWidth: '200px',
                                    maxHeight: '250px',
                                    overflow: 'auto'
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {salesPersons.map(s => (
                                        <span
                                            key={s.id}
                                            onClick={() => handleMultiSelectChange('sales_id', s.id)}
                                            style={{
                                                padding: '4px 10px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                background: filters.sales_id.includes(s.id) ? '#f6ffed' : '#f5f5f5',
                                                border: filters.sales_id.includes(s.id) ? '1px solid #52c41a' : '1px solid #d9d9d9',
                                                color: filters.sales_id.includes(s.id) ? '#52c41a' : '#666',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {filters.sales_id.includes(s.id) && '✓ '}{s.real_name || s.username}
                                        </span>
                                    ))}
                                </div>
                                {filters.sales_id.length > 0 && (
                                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eee', textAlign: 'right' }}>
                                        <span onClick={() => handleClearFilter('sales_id')} style={{ color: '#1890ff', fontSize: '12px', cursor: 'pointer' }}>清除选择</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <button className="btn btn-primary" onClick={handleSearch}>搜索</button>
                <button
                    className={`btn ${isRandomMode ? 'btn-warning' : 'btn-default'}`}
                    onClick={handleRandomSort}
                    title="随机返回10条符合条件的记录"
                >
                    🎲 随机
                </button>
            </div>

            {isRandomMode && (
                <div style={{ marginBottom: '10px', padding: '8px 12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: '4px', color: '#d46b08' }}>
                    🎲 当前为随机模式，显示 {accounts.length} 条随机记录（共 {pagination.total} 条符合条件）
                    <button
                        className="btn btn-small btn-default"
                        onClick={() => fetchAccounts(false)}
                        style={{ marginLeft: '10px' }}
                    >
                        返回正常模式
                    </button>
                </div>
            )}

            {selectedRowKeys.length > 0 && (
                <div style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: exportFormat === 'custom' ? '10px' : '0' }}>
                        <span>已选择 {selectedRowKeys.length} 项</span>

                        {(isAdmin || user.has_role('developer')) && (
                            <select
                                value={batchAction}
                                onChange={e => setBatchAction(e.target.value)}
                                style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                            >
                                <option value="status">修改状态</option>
                                <option value="owner">修改所属人</option>
                            </select>
                        )}

                        {batchAction === 'status' ? (
                            <select value={batchStatus} onChange={e => setBatchStatus(e.target.value)} style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                <option value="">选择目标状态...</option>
                                <option value="pending_upgrade">待升级</option>
                                <option value="pending_sale">待销售</option>
                                <option value="pending_settle">待结算</option>
                                <option value="settled">已结算</option>
                                <option value="self_use">自用</option>
                                <option value="disabled">不可用</option>
                            </select>
                        ) : (
                            <select value={batchOwner} onChange={e => setBatchOwner(e.target.value)} style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                <option value="">选择新所属者...</option>
                                {owners.map(o => <option key={o.id} value={o.id}>{o.real_name || o.username} ({roleMap[o.role]})</option>)}
                            </select>
                        )}

                        <button className="btn btn-primary" onClick={handleBatchUpdate} disabled={batchAction === 'status' ? !batchStatus : !batchOwner}>
                            批量执行
                        </button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                            <span style={{ fontSize: '13px', color: '#666' }}>导出格式：</span>
                            <select
                                value={exportFormat}
                                onChange={e => setExportFormat(e.target.value)}
                                style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px', minWidth: '200px' }}
                            >
                                <option value="format1">格式1: 邮箱：xx 密码：xx 2FA：xxx</option>
                                <option value="format2">格式2: email ---- pwd ---- email2 ---- 2fa</option>
                                <option value="custom">自定义格式</option>
                            </select>
                            <button className="btn btn-success" onClick={handleExport}>
                                📥 导出选中
                            </button>
                        </div>
                    </div>

                    {exportFormat === 'custom' && (
                        <div style={{ paddingTop: '10px', borderTop: '1px solid #dee2e6' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>自定义模板：</span>
                                <input
                                    value={customFormatTemplate}
                                    onChange={e => setCustomFormatTemplate(e.target.value)}
                                    placeholder="例如: {email} ---- {password} ---- {2fa}"
                                    style={{
                                        flex: 1,
                                        padding: '6px 10px',
                                        borderRadius: '4px',
                                        border: '1px solid #ddd',
                                        fontSize: '13px',
                                        fontFamily: 'monospace'
                                    }}
                                />
                            </div>
                            <div style={{
                                marginTop: '8px',
                                padding: '8px',
                                background: '#e7f3ff',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#0066cc'
                            }}>
                                <strong>可用变量：</strong>
                                <span style={{ marginLeft: '8px' }}>
                                    {'{email}'} {'{password}'} {'{two_factor_code}'} {'{2fa}'} {'{recovery_email}'} {'{recovery_phone}'} {'{status}'} {'{developer_name}'} {'{remark}'} {'{id}'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <Table
                columns={columns}
                data={accounts}
                loading={loading}
                rowSelection={rowSelection}
                sort={sort}
                onSort={handleSort}
            />
            <Pagination
                current={pagination.page}
                total={pagination.total}
                pageSize={pagination.per_page}
                onChange={(page) => setPagination(prev => ({ ...prev, page }))}
                onPageSizeChange={(size) => {
                    setPagination(prev => ({ ...prev, per_page: size, page: 1 }));
                }}
            />

            {/* 新建/编辑模态框 */}
            <Modal visible={modalVisible} title={editingAccount ? '编辑账号' : '新建账号'} onClose={() => setModalVisible(false)}
                footer={<><button className="btn btn-default" onClick={() => setModalVisible(false)}>取消</button>
                    <button className="btn btn-primary" onClick={handleSubmit}>确定</button></>}>
                <FormItem label="谷歌邮箱" required>
                    <input value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                </FormItem>
                <FormItem label="密码" required>
                    <input value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} />
                </FormItem>
                <FormItem label="2FA通行码">
                    <input value={formData.two_factor_code || ''} onChange={e => setFormData({ ...formData, two_factor_code: e.target.value })} />
                </FormItem>
                <FormItem label="验证邮箱">
                    <input value={formData.recovery_email || ''} onChange={e => setFormData({ ...formData, recovery_email: e.target.value })} />
                </FormItem>
                <FormItem label="验证手机">
                    <input value={formData.recovery_phone || ''} onChange={e => setFormData({ ...formData, recovery_phone: e.target.value })} />
                </FormItem>
                {isAdmin && (
                    <FormItem label="所属者" required>
                        <select value={formData.developer_id} onChange={e => setFormData({ ...formData, developer_id: parseInt(e.target.value) })}>
                            {owners.map(o => <option key={o.id} value={o.id}>{o.real_name || o.username} ({roleMap[o.role]})</option>)}
                        </select>
                    </FormItem>
                )}
                <FormItem label="状态">
                    <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                        <option value="pending_upgrade">待升级</option>
                        <option value="pending_sale">待销售</option>
                        <option value="pending_settle">待结算</option>
                        <option value="settled">已结算</option>
                        <option value="self_use">自用</option>
                        <option value="disabled">不可用</option>
                    </select>
                </FormItem>
                <FormItem label="备注">
                    <textarea value={formData.remark || ''} onChange={e => setFormData({ ...formData, remark: e.target.value })} />
                </FormItem>
            </Modal>

            {/* 批量导入模态框 */}
            <Modal visible={importModalVisible} title="批量导入账号" onClose={() => setImportModalVisible(false)}
                footer={<><button className="btn btn-default" onClick={() => setImportModalVisible(false)}>取消</button>
                    <button className="btn btn-primary" onClick={handleImport}>导入</button></>}>
                {isAdmin && (
                    <FormItem label="所属者" required>
                        <select value={importData.developer_id} onChange={e => setImportData({ ...importData, developer_id: parseInt(e.target.value) })}>
                            {owners.map(o => <option key={o.id} value={o.id}>{o.real_name || o.username} ({roleMap[o.role]})</option>)}
                        </select>
                    </FormItem>
                )}
                <FormItem label="导入内容" required>
                    <textarea
                        rows={10}
                        value={importData.text}
                        onChange={e => setImportData({ ...importData, text: e.target.value })}
                        placeholder={`每行一条记录，格式：账户 密码 验证邮箱 2FA [状态]\n例如：\ntest@gmail.com password123 recovery@mail.com 2facode123 待销售\ntest2@gmail.com pass456 rec2@mail.com 2fa456\n支持空格或----分割`}
                        style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                </FormItem>
                <p style={{ color: '#999', fontSize: '12px' }}>
                    支持多行导入，每行格式：邮箱 密码 验证邮箱 2FA [状态]<br />
                    支持空格或----分割，状态可选值：待销售(对应pending_sale)、已结算、自用、待升级、不可用/问题号
                </p>
            </Modal>

            {/* 导入错误模态框 */}
            <Modal visible={errorModalVisible} title="导入结果" onClose={() => setErrorModalVisible(false)}
                footer={<button className="btn btn-primary" onClick={() => setErrorModalVisible(false)}>确定</button>}>
                <div>
                    <p style={{ marginBottom: '10px' }}>导入完成，但部分记录存在错误：</p>
                    <div style={{ maxHeight: '300px', overflow: 'auto', background: '#fff0f0', border: '1px solid #ffccc7', padding: '10px', borderRadius: '4px' }}>
                        {importErrors.map((err, i) => (
                            <div key={i} style={{ color: '#cf1322', fontSize: '12px', marginBottom: '4px', borderBottom: '1px dashed #ffccc7', paddingBottom: '2px' }}>
                                {err}
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>

            {/* 详情模态框 */}
            <Modal visible={detailModalVisible} title="账号详情" onClose={() => setDetailModalVisible(false)}
                footer={<button className="btn btn-default" onClick={() => setDetailModalVisible(false)}>关闭</button>}>
                {detailAccount && (
                    <div style={{ lineHeight: '2' }}>
                        <p><strong>ID：</strong>{detailAccount.id}</p>
                        <p><strong>谷歌邮箱：</strong>{detailAccount.email}</p>
                        <p><strong>密码：</strong>{detailAccount.password}</p>
                        <p><strong>2FA通行码：</strong>{detailAccount.two_factor_code || '-'}</p>
                        <p><strong>验证邮箱：</strong>{detailAccount.recovery_email || '-'}</p>
                        <p><strong>验证手机：</strong>{detailAccount.recovery_phone || '-'}</p>
                        {isAdmin && <p><strong>所属者：</strong>{detailAccount.developer_name || '-'}</p>}
                        <p><strong>状态：</strong><span className={`tag ${statusMap[detailAccount.status]?.class}`}>{statusMap[detailAccount.status]?.text}</span></p>
                        <p><strong>备注：</strong>{detailAccount.remark || '-'}</p>
                        <hr style={{ margin: '15px 0', border: 'none', borderTop: '1px solid #eee' }} />
                        <p><strong>创建人：</strong>{detailAccount.created_by_name || '-'}</p>
                        <p><strong>创建时间：</strong>{formatDate(detailAccount.created_at)}</p>
                        <p><strong>更新人：</strong>{detailAccount.updated_by_name || '-'}</p>
                        <p><strong>更新时间：</strong>{formatDate(detailAccount.updated_at)}</p>
                    </div>
                )}
            </Modal>

            {/* 快捷销售模态框 */}
            <Modal
                visible={quickSaleModalVisible}
                title={`快捷销售 - ${quickSaleAccount?.email || ''}`}
                onClose={() => setQuickSaleModalVisible(false)}
                footer={
                    <>
                        <button className="btn btn-default" onClick={() => setQuickSaleModalVisible(false)}>取消</button>
                        <button className="btn btn-primary" onClick={handleQuickSale}>确认销售</button>
                    </>
                }
            >
                <div style={{ marginBottom: '15px', padding: '10px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                    <strong>绑定账号：</strong>{quickSaleAccount?.email}<br />
                    <small style={{ color: '#666' }}>销售成功后，账号将自动变为"待结算"状态</small>
                </div>

                <FormItem label="客户名称">
                    <input
                        value={quickSaleData.customer_name}
                        onChange={e => setQuickSaleData({ ...quickSaleData, customer_name: e.target.value })}
                        placeholder="输入客户名称"
                    />
                </FormItem>
                <FormItem label="客户邮箱" required>
                    <input
                        value={quickSaleData.customer_email}
                        onChange={e => setQuickSaleData({ ...quickSaleData, customer_email: e.target.value })}
                        placeholder="输入客户邮箱"
                    />
                </FormItem>
                <FormItem label="销售金额" required>
                    <input
                        type="number"
                        value={quickSaleData.amount}
                        onChange={e => setQuickSaleData({ ...quickSaleData, amount: e.target.value })}
                        placeholder="输入销售金额"
                    />
                </FormItem>
                <FormItem label="销售人员">
                    <select
                        value={quickSaleData.sales_id}
                        onChange={e => setQuickSaleData({ ...quickSaleData, sales_id: parseInt(e.target.value) })}
                    >
                        <option value={user.id}>{user.real_name || user.username} (我自己)</option>
                        {salesPersons.filter(s => s.id !== user.id).map(s => (
                            <option key={s.id} value={s.id}>{s.real_name || s.username}</option>
                        ))}
                    </select>
                </FormItem>
                <FormItem label="备注">
                    <textarea
                        value={quickSaleData.remark}
                        onChange={e => setQuickSaleData({ ...quickSaleData, remark: e.target.value })}
                        placeholder="可选备注信息"
                    />
                </FormItem>
            </Modal>
        </div>
    );
};

export default AccountManager;
